import React, {
  ReactElement, ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router';
import Context, { ConfirmActionContextType } from './ConfirmActionContext';
import { useSuccessMessageContext } from '../../SuccessMessageContext';
import ConfirmActionType from '../../../model/ConfirmActionType';

interface Props {
  children: ReactNode;
}

const ConfirmActionContextProvider = ({ children }: Props): ReactElement => {
  const [visible, setVisible] = useState<boolean>(false);
  const [action, setAction] = useState<() => { /* noop */ }>(null);
  const [confirmationText, setConfirmationText] = useState<string>(null);
  const [confirmActionType, setConfirmActionType] = useState<ConfirmActionType>(null);
  const { clearMessages } = useSuccessMessageContext();
  const currentLocation = useLocation();

  useEffect(() => {
    setVisible(false);
    setAction(null);
    setConfirmActionType(null);
    setConfirmationText(null);
  }, [currentLocation]);

  const scrollTop = (): void => {
    const { top } = document.getElementById('confirm-action-wrapper').getBoundingClientRect();
    window.parent.postMessage({
      type: 'scroll-to',
      top,
    });
  };

  const handleAction = useCallback((onAction, text, actionType) => {
    setVisible(true);
    clearMessages();
    setConfirmationText(text);
    setAction(onAction);
    setConfirmActionType(actionType);
    scrollTop();
  }, [clearMessages]);

  const cancelAction = (): void => {
    setVisible(false);
    setAction(null);
    setConfirmActionType(null);
    setConfirmationText(null);
  };

  const confirmAction = useCallback((): void => {
    action();
    setVisible(false);
    setConfirmActionType(null);
    setConfirmationText(null);
  }, [action]);

  const value: ConfirmActionContextType = useMemo(() => ({
    handleAction,
    confirmAction,
    cancelAction,
    visible,
    confirmationText,
    confirmActionType,
  }), [confirmAction, confirmActionType, confirmationText, handleAction, visible]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export default ConfirmActionContextProvider;
